import { motion, AnimatePresence } from "framer-motion";
import { X, Linkedin, Mail, Globe, GraduationCap, Clock, BookOpen } from "lucide-react";
import { resolveTrainerImage, trainerInitials, trainerImageStyle } from "@/lib/trainers";

/**
 * Lightweight "View Profile" modal for a trainer/member.
 * No dedicated trainer detail page/route exists in this app, so per the brief's own
 * fallback instruction, this reuses the existing member data (no new fields beyond
 * `subjects`/`website`, already added to the members endpoint) rather than a new page.
 */
export default function TrainerProfileModal({ trainer, onClose }) {
  return (
    <AnimatePresence>
      {trainer && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={`${trainer.name} profile`}
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto grid md:grid-cols-[240px,1fr]"
            onClick={(e) => e.stopPropagation()}
            data-testid={`trainer-modal-${trainer.id}`}
          >
            <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-gray-100 relative">
              {trainer.photo ? (
                <img
                  src={resolveTrainerImage(trainer.photo)}
                  alt={trainer.name}
                  className="w-full h-full object-cover"
                  style={trainerImageStyle(trainer)}
                />
              ) : (
                <div className="w-full h-full grid place-items-center bg-gradient-to-br from-indigo-100 via-white to-emerald-100">
                  <span className="font-display text-4xl font-semibold text-[#0F172A]/40">
                    {trainerInitials(trainer.name)}
                  </span>
                </div>
              )}
            </div>

            <div className="p-6 sm:p-8 relative">
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>

              <h2 className="font-display text-2xl font-semibold text-[#0F172A] pr-8">{trainer.name}</h2>
              <div className="text-sm text-gray-500 mt-1">
                {[trainer.designation, trainer.department].filter(Boolean).join(" · ")}
              </div>
              {trainer.experience && (
                <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                  <Clock className="w-3.5 h-3.5" /> {trainer.experience}
                </div>
              )}

              {trainer.bio && <p className="mt-5 text-sm text-gray-700 leading-relaxed">{trainer.bio}</p>}

              {trainer.subjects?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Subjects</div>
                  <div className="flex flex-wrap gap-1.5">
                    {trainer.subjects.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs font-medium text-gray-700">
                        <BookOpen className="w-3 h-3" /> {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {trainer.qualifications?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Qualifications & Certifications</div>
                  <div className="flex flex-wrap gap-1.5">
                    {trainer.qualifications.map((q, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-xs font-medium border border-indigo-100">
                        <GraduationCap className="w-3 h-3" /> {q}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 pt-5 border-t border-gray-100 flex flex-wrap items-center gap-3">
                {trainer.linkedin && (
                  <a href={trainer.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:underline">
                    <Linkedin className="w-4 h-4" /> LinkedIn
                  </a>
                )}
                {trainer.website && (
                  <a href={trainer.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:underline">
                    <Globe className="w-4 h-4" /> Website
                  </a>
                )}
                {trainer.email && (
                  <a href={`mailto:${trainer.email}`} className="inline-flex items-center gap-1.5 bg-[#050914] hover:bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 rounded-full transition-all ml-auto">
                    <Mail className="w-4 h-4" /> Get in touch
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
